/**
 * useVoiceChat Hook
 * Manages voice chat functionality for HeyGen avatar
 */

import { useCallback } from 'react';
import { useStreamingAvatarContext } from '../context/StreamingAvatarContext';

export const useVoiceChat = () => {
  const {
    avatarRef,
    isMuted,
    setIsMuted,
    isVoiceChatActive,
    setIsVoiceChatActive,
    isVoiceChatLoading,
    setIsVoiceChatLoading,
  } = useStreamingAvatarContext();

  // Start voice chat
  const startVoiceChat = useCallback(
    async (isInputAudioMuted: boolean = false) => {
      if (!avatarRef.current) return;
      setIsVoiceChatLoading(true);
      try {
        await avatarRef.current.voiceChat.start({
          defaultMuted: isInputAudioMuted,
        });
        setIsVoiceChatActive(true);
        setIsMuted(isInputAudioMuted);
      } catch (_error) {
      } finally {
        setIsVoiceChatLoading(false);
      }
    },
    [avatarRef, setIsVoiceChatLoading, setIsVoiceChatActive, setIsMuted],
  );

  const stopVoiceChat = useCallback(() => {
    if (!avatarRef.current) return;
    try {
      avatarRef.current.voiceChat.stop();
    } catch (_e) {
      // Ignore errors if already stopped
    }
    setIsVoiceChatActive(false);
    setIsMuted(true);
  }, [avatarRef, setIsMuted, setIsVoiceChatActive]);

  const muteInputAudio = useCallback(async () => {
    if (!avatarRef.current) return;
    await avatarRef.current.voiceChat.mute();
    setIsMuted(true);
  }, [avatarRef, setIsMuted]);

  const unmuteInputAudio = useCallback(async () => {
    if (!avatarRef.current) return;
    await avatarRef.current.voiceChat.unmute();
    setIsMuted(false);
  }, [avatarRef, setIsMuted]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (isMuted) {
      unmuteInputAudio();
    } else {
      muteInputAudio();
    }
  }, [isMuted, muteInputAudio, unmuteInputAudio]);

  return {
    startVoiceChat,
    stopVoiceChat,
    muteInputAudio,
    unmuteInputAudio,
    toggleMute,
    isMuted,
    isVoiceChatActive,
    isVoiceChatLoading,
  };
};
